import { useEffect, useRef, useState } from 'react';

const STORAGE_PREFIX = 'listFilters:';

const storageKey = (persistKey) => `${STORAGE_PREFIX}${persistKey}`;

export const readListFilters = (persistKey) => {
  if (!persistKey || typeof sessionStorage === 'undefined') {
    return undefined;
  }

  try {
    const raw = sessionStorage.getItem(storageKey(persistKey));
    if (!raw) {
      return undefined;
    }

    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

export const writeListFilters = (persistKey, value) => {
  if (!persistKey || typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(storageKey(persistKey), JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures
  }
};

/** Remove all persisted list filter keys (e.g. on logout) so they do not leak across accounts. */
export const clearListFilters = () => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
};

const resolveDefault = (defaultValue) => (typeof defaultValue === 'function' ? defaultValue() : defaultValue);

const mergeStored = (defaults, stored) => {
  if (stored === undefined) {
    return defaults;
  }
  if (defaults !== null && typeof defaults === 'object' && !Array.isArray(defaults) && typeof stored === 'object') {
    return { ...defaults, ...stored };
  }
  return stored;
};

/**
 * Persist state in sessionStorage when persistKey is set.
 * When persistKey is omitted, behaves like useState(defaultValue).
 */
const useSessionPersistedState = (persistKey, defaultValue) => {
  const [state, setState] = useState(() => {
    if (!persistKey) {
      return resolveDefault(defaultValue);
    }
    return mergeStored(resolveDefault(defaultValue), readListFilters(persistKey));
  });
  const previousPersistKeyRef = useRef(persistKey);

  useEffect(() => {
    if (previousPersistKeyRef.current !== persistKey) {
      previousPersistKeyRef.current = persistKey;
      if (!persistKey) {
        setState(resolveDefault(defaultValue));
        return;
      }
      setState(mergeStored(resolveDefault(defaultValue), readListFilters(persistKey)));
      return;
    }

    if (persistKey) {
      writeListFilters(persistKey, state);
    }
  }, [persistKey, state, defaultValue]);

  return [state, setState];
};

export default useSessionPersistedState;
